import React from 'react';
import { useRouteMatch, Switch, Redirect, useHistory } from 'react-router-dom';
import AuthorizedRoute from '../../../../permission/AuthorizedRoute';
import { BulkRequestList } from '../../../../components/bulkRequest';
import { PAGE_NAMES, PERMISSIONS_MANAGEMENT_RESOURCES, SETTINGS_V2_ACTION } from '../../../../settingsV2Constant';
import usePermission from '../../../../permission/usePermission';
import { ENTITIES } from './constants';
import { ACTION_IDS } from '../../../../components/bulkRequest/actionIds';
import CreateRequest from './submodules/createRequest';
import useService from './utils/useService';
import { columnConfigs, statusColumnConfigs } from './listConfig';
import { DEFAULT_CONFIG } from './helper';
import { VIEW_STATUS_ACTION_NAME, CREATE_ACTION_NAME } from '../../../../components/bulkRequest/constants';

const makeActions = (hasPermission: ReturnType<typeof usePermission>['hasPermission']) => {
	const withPerm = (actionName: string) =>
		hasPermission({
			configPageName: PAGE_NAMES.permissionsManagement,
			configResourceName: PERMISSIONS_MANAGEMENT_RESOURCES.bulkPermissionCsv,
			configActionName: actionName
		});

	return {
		bulkActions: [],
		rowActions: [
			{
				name: VIEW_STATUS_ACTION_NAME,
				id: ACTION_IDS.VIEW_STATUS,
				isEnabled: () => withPerm(SETTINGS_V2_ACTION.checkBulkPermissionCsvRequestStatus)
			}
		],
		addAction: {
			name: CREATE_ACTION_NAME,
			id: ACTION_IDS.ADD,
			isEnabled: () => withPerm(SETTINGS_V2_ACTION.createBulkPermissionCsvRequest)
		}
	};
};

const usePageActions = () => {
	const history = useHistory();
	const { hasPermission } = usePermission();

	const getActionHandler = (action: any) => {
		switch (action.id) {
			case ACTION_IDS.ADD:
				return () => {
					const basePath = history.location.pathname.replace(/\/list$/, '');
					history.push(`${basePath}/create`);
				};
			case ACTION_IDS.VIEW_STATUS:
				return () => {};
			default:
				return () => {};
		}
	};

	return {
		getActionHandler,
		getActionList: () => makeActions(hasPermission)
	};
};

const BulkPermissionCsvDashboard: React.FC = () => {
	const { path } = useRouteMatch();
	const service = useService();
	const { getActionHandler, getActionList } = usePageActions();
	const actions = getActionList();

	return (
		<Switch>
			<AuthorizedRoute
				configPageName={PAGE_NAMES.permissionsManagement}
				configResourceName={PERMISSIONS_MANAGEMENT_RESOURCES.bulkPermissionCsv}
				configActionName={SETTINGS_V2_ACTION.listBulkPermissionCsvRequests}
				path={`${path}/list`}
				component={() => (
					<BulkRequestList
						defaults={DEFAULT_CONFIG}
						fetchData={service.listRequests}
						statusFetchData={service.checkStatus}
						columnConfigs={columnConfigs}
						statusColumnConfigs={statusColumnConfigs}
						entity={ENTITIES.BULK_PERMISSIONS_CSV}
						statusEntity={ENTITIES.BULK_PERMISSIONS_CSV_STATUS}
						forceUpdate={service.forceUpdate}
						turnForceUpdateOff={service.turnForceUpdateOff}
						actions={actions}
						getActionHandler={getActionHandler}
					/>
				)}
				exact
			/>
			<AuthorizedRoute
				configPageName={PAGE_NAMES.permissionsManagement}
				configResourceName={PERMISSIONS_MANAGEMENT_RESOURCES.bulkPermissionCsv}
				configActionName={SETTINGS_V2_ACTION.createBulkPermissionCsvRequest}
				path={`${path}/create`}
				component={CreateRequest}
				exact
			/>
			<Redirect to={`${path}/list`} />
		</Switch>
	);
};

export default BulkPermissionCsvDashboard;
